<script setup lang="ts">
import { watchEffect, ref, nextTick, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useWindowSize } from '@vueuse/core'

import { type WithAttributes, type Labeled, toHtml, fw } from './attributes'

import { useContext } from './context'

import Button from '~/ui/Button.vue'
import Spacer from '~/ui/Spacer.vue'

// #region props
const {
  priority = 0,
  ...props
} = defineProps<WithAttributes<{
  class: {
    color?: 'color-primary-solid' | 'color-destructive-solid' | 'color-default-solid'
  }
  title?: string
  isDestructive?: boolean
  cancel?: string | true
  icon?: string
  autofocus?: true | 'off'
  maximizeSize?: boolean
  priority?: number
  uiRaised?: boolean

  /**
     * Defines the element's role in the accessibility tree.
     *
     * Only use when the default role of a semantic element is not enough.
     */
  role?: string
} & Labeled>>()
// #endregion props

// TODO: Refactor width calculation and defaults, and allow arbitrary widths

const { width: screenWidth } = useWindowSize()

const { t } = useI18n()

// #region model
const isOpen = defineModel<boolean>()
// #endregion model

// maxWidth: the maximum width of the modal, given the current screen size
// TODO: Use @container queries to avoid this scripting
const width = computed(() => {
  const padding = 32;
  const gap = 32;
  const column = 46;
  const minMargin = 4
  const maxColumnsPerScreen = Math.trunc((screenWidth.value - 2 * padding - 2 * minMargin + gap) / (column + gap))

  const maxNumberOfCards = props.maximizeSize ? 10 : 4
  const cardsThatFitOnScreen = Math.trunc(maxColumnsPerScreen / 3)

  const columns = Math.min(maxNumberOfCards, cardsThatFitOnScreen) * 3

  const max = `${columns * column + (columns - 1) * gap + padding * 2}px`

  return ({ columns, max })
})

const previouslyFocusedElement = ref()

// Handle focus and inertness of the elements behind the modal
watchEffect(() => {
  if (typeof window === 'undefined') return // SSR early return
  if (isOpen.value) {
    nextTick(() => {
      previouslyFocusedElement.value = document.activeElement
      if (props.autofocus !== 'off') {
        previouslyFocusedElement.value?.blur()
      }
      document.querySelector('#app')?.setAttribute('inert', 'true')
    })
  } else {
    nextTick(() => previouslyFocusedElement.value?.focus())
    document.querySelector('#app')?.removeAttribute('inert')
  }
})

// All nested elements bleed inline. Components that accept raising or sinking are also affected.
useContext({ uiBleedInline: true, uiSunken: true, uiRaised: true })

// TODO:
// Smaller paddings in constrained container inline-width context
</script>

<template>
  <Teleport to="body">
    <Transition>
      <div
        v-if="isOpen"
        class="funkwhale overlay"
        :style="`z-index: calc(9001 + ${priority});`"
        @click.exact.stop="isOpen = false"
      >
        <div
          v-bind="toHtml<'div'>(props)"
          class="funkwhale modal" :class="[
            fw.shape.modal,
            !props.class?.includes('color-') && fw.color.defaultSolid,
            props.class,
            isDestructive && 'isdestructive',
            !!$slots.alert && 'has-alert'
          ]"
          tabindex="-1"
          @keyup.escape.exact.stop="isOpen = false"
          @click.stop
        >
          <div
            :class="[fw.layout.row, fw.align.start]"
            style="padding: 12px 12px 0 12px;"
          >
            <div
              v-if="!$slots.topleft && !icon"
              style="width: 48px;"
            />
            <div
              v-if="icon"
              style="display: flex; justify-content: center; align-items: center; width: 48px;"
            >
              <Icon
                :icon
                style="font-size: 18px;"
              />
            </div>
            <slot name="topleft" />
            <Spacer
              v-if="!$slots.topleft"
              grow
            />
            <h2
              v-if="title !== ''"
              :class="[fw.font.sectionHeading, isDestructive && fw.color.destructive]"
              style="margin: 0"
            >
              {{ title }}
            </h2>
            <Spacer
              v-if="title !== ''"
              grow
            />
            <Button
              class="color-default-ghost shape-square"
              :aria-label="t('vui.aria.close')"
              :autofocus="props.autofocus === undefined ? ($slots.actions || cancel ? undefined : true) : props.autofocus !== 'off'"
              @click="isOpen = false"
            >
              <Icon icon="bi:x-lg" />
            </Button>
          </div>

          <!-- Content -->

          <div class="modal-shadow-top" />

          <div class="modal-content padding-modal">
            <Transition>
              <div
                v-if="$slots.alert"
                class="alert-container"
              >
                <div>
                  <slot name="alert" />
                </div>
              </div>
            </Transition>

            <slot
              :columns="width.columns"
              :cards-per-row="(numberOfColumns = 3) => Math.trunc(width.columns / numberOfColumns)"
              :activities-per-row="(numberOfColumns = 4) => Math.trunc(width.columns / numberOfColumns)"
              :close="() => { isOpen = false }"
            />

            <Spacer v-if="!$slots.actions" />
          </div>

          <div class="modal-shadow-bottom" />

          <!-- Actions slot -->

          <div
            v-if="$slots.actions || cancel"
            class="modal-actions" :class="[fw.layout.row]"
            ui-gap="12"
          >
            <Button
              v-if="cancel"
              autofocus
              ui-raised
              @click="() => { isOpen = false }"
            >
              {{ typeof cancel === 'string' ? cancel : t('vui.cancel') }}
            </Button>
            <slot name="actions" />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style>
.funkwhale.modal {
  box-shadow: 0 2px 12px 2px var(--color-shadow);
  border-radius: 1rem;
  max-width: v-bind("width.max");
  width: 100%;

  display: grid;
  max-height: 90vh;
  min-height: v-bind("maximizeSize ? '90vh' : '2rem'");
  grid-template-rows: auto 1fr auto;

  position: relative;

  &.isdestructive {
    border-top: 24px solid var(--fw-red-500);

    h2.destructive-header {
        color: var(--fw-red-500);
    }
  }

  > h2 {
    font-size: 1.25em;
    padding: 1.625rem 4.5rem;
    line-height: 1.2;
    text-align: center;
    position: relative;

    > .funkwhale.button {
      font-size: 1rem;
      position: absolute;
      right: 1rem;
      top: 50%;
      transform: translateY(-49%);
    }
  }

  .modal-content {
    overflow: auto;
    position: relative;

    > .alert-container {
      position: sticky;
      top: -1rem;
      margin: -1rem -2rem 1rem;

      display: grid;
      grid-template-rows: 1fr;

      &.v-enter-active,
      &.v-leave-active {
        transition: grid-template-rows 0.2s ease;
      }

      &.v-enter-from,
      &.v-leave-to {
        grid-template-rows: 0fr;
      }

      > div {
        overflow: hidden;
      }
    }
  }

  .modal-shadow-top {
    z-index: 2;
    height: 16px;
    width: 100%;
    position: absolute;
    top: 60px;
    background:linear-gradient(var(--color-background), color-mix(in oklab, var(--color-background) 50%, transparent), color-mix(in oklab, var(--color-background) 20%, transparent), transparent);
  }

  .modal-shadow-bottom {
    height: 32px;
    margin-top: -32px;
    position: sticky;
    bottom: 0px;
    background:linear-gradient(transparent, color-mix(in oklab, var(--background-color) 20%, transparent), color-mix(in oklab, var(--background-color) 50%, transparent), var(--background-color));
    &:last-child{
      border-bottom-right-radius: 1rem;
      border-bottom-left-radius: 1rem;
    }
  }

  .modal-actions {
    padding: 0 2rem 1rem 2rem;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;

    > :first-child {
      margin-left: 0px !important;
    }

    > :last-child {
      margin-right: 0px !important;
    }

    position: relative;
  }
}

.funkwhale.overlay {
  background: rgba(0,0,0, .3);

  position: fixed;
  inset: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  &.v-enter-active,
  &.v-leave-active {
    transition: opacity 0.2s ease;

    .funkwhale.modal {
      transition: transform 0.2s ease;
    }
  }

  &.v-enter-from,
  &.v-leave-to {
    opacity: 0;

    .funkwhale.modal {
      transform: translateY(1rem);
    }
  }

  &.v-leave-to {
    .funkwhale.modal {
      transform: translateY(-1rem);
    }
  }
}
</style>
